// Astron Diamond - Mock Data
// ============================================================
// HOW TO UPDATE CONTENT & IMAGES:
// 
// 1. IMAGES: Simply replace the URL strings below with your new image URLs.
//    Upload images to any CDN (Cloudinary, S3, etc.) and paste the URL here.
//    Example: Change "https://images.unsplash.com/..." to "https://your-cdn.com/new-image.jpg"
//
// 2. TEXT: Edit any string values directly. All website text is in this file.
//    Search for the section you want to change (e.g. "heroHeadline" for the main heading).
//
// 3. After saving changes, the site refreshes automatically (hot reload).
// ============================================================

export const siteInfo = {
  name: "Astron Diamond",
  tagline: "Manufacturing Since 2016",
  logo: "/logo.svg",
  address: "38 W 48th Street, Suite 905, New York, NY 10036",
  phone: "+1 (212) 221-1028",
  email: "custom@astrondiamond.com",
  description: "We design and manufacture fine jewelry, from eternity bands, engagement rings to statement pieces and custom projects."
};

// ---- NAVIGATION ----
// Collections tab is hidden for non-logged-in users (controlled in Navbar component)
export const navLinks = [
  { label: "About Us", path: "/about-us" },
  {
    label: "Factory",
    children: [
      { label: "Jewelry Manufacturing", path: "/jewelry-manufacturing" },
      { label: "Diamond Manufacturing", path: "/diamond-manufacturing" }
    ]
  },
  {
    label: "Collections",
    requiresAuth: true,
    children: [
      { label: "Jewelry", path: "/jewelry" },
      { label: "Diamonds", path: "/diamonds" }
    ]
  },
  {
    label: "Connect With Us",
    children: [
      { label: "Contact Us", path: "/contact-us" },
      { label: "Make an Appointment", path: "/make-an-appointment" }
    ]
  },
  { label: "Ethical Standards", path: "/ethical-standards" }
];

// ---- JBT BADGE (only certification shown) ----
export const jbtBadge = {
  name: "Jewelers Board of Trade",
  shortName: "JBT",
  logo: "/jbt-logo.svg"
};

// ---- HERO SECTION ----
export const heroContent = {
  headline: "Step into the Light with",
  brandName: "Astron Diamond",
  // Replace this URL to change the hero background image
  backgroundImage: "https://images.pexels.com/photos/2735981/pexels-photo-2735981.jpeg?auto=compress&w=1920&h=1080&fit=crop"
};

// ---- "WHERE QUALITY MEETS VISIONARY GROWTH" SECTION ----
export const brandSection = {
  heading: "Where Quality Meets Visionary Growth",
  paragraph: "In the jewelry world, a brand's brilliance is defined by its identity. At Astron Diamond, we don't just supply; we scale. We provide the strategic solutions and premium craftsmanship necessary to elevate your market presence and captivate your audience. Consider us the bedrock of your brand's long-term success.",
  blocks: [
    {
      title: "Beyond the Transaction",
      description: "We don't just fill orders; we fuel brands. At Astron Diamond, every client receives the full weight of our design and manufacturing expertise. We guide you through our curated collections of diamond jewelry, ensuring that every piece you stock reinforces your reputation for quality and drives long-term customer loyalty."
    },
    {
      title: "Partner First",
      description: "We believe that our reputation is built on your success. Our promise is simple: to provide premium craftsmanship and honest communication without exception. We want you to feel the same pride in showcasing our collections as we do in creating them—knowing that every piece meets the highest industry standards for quality and ethics."
    }
  ]
};

// ---- ELEGANCE SECTIONS (full-width parallax images) ----
export const eleganceSections = [
  {
    heading: "Mastered by Hand.",
    subheading: "Defined by Detail",
    paragraph: "From the initial sketch to the final polish, we obsess over the details so you don't have to. Experience the intersection of rigorous standards and breathtaking beauty.",
    // Pointing to local public image
    image: "/custom-rings.jpg"
  },
  {
    heading: "Built for Life.",
    subheading: "Born for Brilliance.",
    paragraph: "Jewelry designed to be lived in, not just looked at. We blend structural resilience with light-catching precision, creating pieces that transition seamlessly from your quietest moments to your grandest celebrations.",
    // Pointing to local public image
    image: "/custom-bracelets.jpg"
  }
];

// ---- "FROM NEW YORK" SECTION ----
export const nySection = {
  heading: "Designed in the City That Never Sleeps.",
  paragraph: "From our studio in the heart of New York City, we redefine what a diamond can be. Inspired by the skyline and driven by the spirit of innovation, we craft jewelry that captures the bold essence of NYC and the timeless beauty of light.",
  // Replace this URL to change the NY image
  image: "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?w=1200&h=800&fit=crop"
};

// ---- CATEGORIES ----
export const categories = [
  {
    name: "Eternity Bands",
    image: "https://images.unsplash.com/photo-1742240439165-60790db1ee93?w=400&h=400&fit=crop"
  },
  {
    name: "Engagement Rings",
    image: "https://images.pexels.com/photos/2849742/pexels-photo-2849742.jpeg?auto=compress&w=400&h=400&fit=crop"
  },
  {
    name: "Fashion Rings",
    image: "/rings-hero.jpg"
  },
  {
    name: "Bracelets",
    image: "/bracelet-hero.jpg"
  },
  {
    name: "Pendants",
    image: "https://images.pexels.com/photos/2735981/pexels-photo-2735981.jpeg?auto=compress&w=400&h=400&fit=crop"
  },
  {
    name: "Necklaces",
    image: "https://images.unsplash.com/photo-1769909953707-31840816c5b3?w=400&h=400&fit=crop"
  },
  {
    name: "Earrings",
    image: "/earrings-hero.jpg"
  },
  {
    name: "Loose Diamonds",
    image: "https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3?w=400&h=400&fit=crop"
  },
  {
    name: "Custom",
    image: "https://images.unsplash.com/photo-1770722272510-ef28c6f57541?w=400&h=400&fit=crop"
  }
];

// ---- CUSTOMIZATION SECTION ----
export const customizationSection = {
  label: "Customization",
  heading: "Your Vision, Our Craft",
  paragraph: "The most meaningful jewelry begins with a conversation. We partner with you to refine your ideas—transforming raw concepts into breathtaking reality. From initial render to final polish, our master artisans spend weeks perfecting every angle. With a relentless commitment to quality and a multi-point certification process, we ensure your custom piece is as flawless as the moment it commemorates.",
  ctaText: "Start Your Custom Project",
  // Replace this URL to change the customization background image
  image: "https://images.unsplash.com/photo-1772442125267-7640b4b5f2fe?w=1920&h=1080&fit=crop"
};

// ---- ETHICAL PRACTICES (homepage) ----
export const ethicalSection = {
  label: "Our Commitment",
  heading: "Ethical Practices",
  paragraph: "We are committed to ethical practices, respecting human rights, social responsibility, and environmental protection throughout our supply chain. Every diamond we source is conflict-free, and every step of our process adheres to the highest industry standards.",
  points: [
    "Conflict-Free Diamonds",
    "Sustainable Sourcing",
    "Fair Labor Practices",
    "Environmental Responsibility"
  ]
};

// ---- ABOUT PAGE ----
export const aboutData = {
  history: "Founded in 2016, Astron Diamond has grown from a small diamond cutting workshop in New York's Diamond District to a globally recognized manufacturer of fine jewelry and diamonds. Our journey of over 60 years has been marked by relentless pursuit of excellence, innovation in craftsmanship, and unwavering commitment to ethical practices.",
  mission: "To create exceptional jewelry that celebrates life's most precious moments, while maintaining the highest standards of quality, sustainability, and customer satisfaction.",
  teamSize: "40+",
  yearsExperience: "60+",
  piecesCreated: "100,000+",
  globalPartners: "500+",
  office: {
    city: "New York",
    address: "38 W 48th Street, Suite 905, New York, NY 10036",
    phone: "+1 (212) 221-1028",
    email: "custom@astrondiamond.com",
    description: "Our headquarters and jewelry manufacturing center"
  }
};

// ---- JEWELRY PRODUCTS ----
export const jewelryProducts = [
  {
    id: 1,
    name: "Classic Solitaire Ring",
    category: "Engagement Rings",
    metal: "18K White Gold",
    stone: "Round Brilliant Diamond",
    image: "https://images.pexels.com/photos/2849742/pexels-photo-2849742.jpeg?auto=compress&w=500&h=500&fit=crop"
  },
  {
    id: 2,
    name: "Eternity Band",
    category: "Eternity Bands",
    metal: "Platinum",
    stone: "Round Diamonds",
    image: "https://images.unsplash.com/photo-1742240439165-60790db1ee93?w=500&h=500&fit=crop"
  },
  {
    id: 3,
    name: "Halo Pendant",
    category: "Pendants",
    metal: "14K Rose Gold",
    stone: "Pear Shaped Diamond",
    image: "https://images.pexels.com/photos/2735981/pexels-photo-2735981.jpeg?auto=compress&w=500&h=500&fit=crop"
  },
  {
    id: 4,
    name: "Tennis Bracelet",
    category: "Bracelets",
    metal: "18K White Gold",
    stone: "Round Brilliant Diamonds",
    image: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=500&h=500&fit=crop"
  },
  {
    id: 5,
    name: "Three Stone Ring",
    category: "Engagement Rings",
    metal: "Platinum",
    stone: "Emerald Cut Diamond",
    image: "https://images.pexels.com/photos/2732096/pexels-photo-2732096.jpeg?auto=compress&w=500&h=500&fit=crop"
  },
  {
    id: 6,
    name: "Diamond Studs",
    category: "Earrings",
    metal: "14K White Gold",
    stone: "Round Brilliant Diamonds",
    image: "https://images.pexels.com/photos/6358515/pexels-photo-6358515.jpeg?auto=compress&w=500&h=500&fit=crop"
  },
  {
    id: 7,
    name: "Riviera Necklace",
    category: "Necklaces",
    metal: "18K Yellow Gold",
    stone: "Graduated Diamonds",
    image: "https://images.unsplash.com/photo-1769909953707-31840816c5b3?w=500&h=500&fit=crop"
  },
  {
    id: 8,
    name: "Vintage Fashion Ring",
    category: "Fashion Rings",
    metal: "18K Rose Gold",
    stone: "Cushion Cut Diamond",
    image: "https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3?w=500&h=500&fit=crop"
  }
];

// ---- DIAMOND PRODUCTS ----
export const diamondProducts = [
  {
    id: 1,
    shape: "Round Brilliant",
    caratRange: "0.30 - 5.00 ct",
    quality: "D-H Color, IF-VS2",
    image: "https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3?w=500&h=500&fit=crop"
  },
  {
    id: 2,
    shape: "Princess Cut",
    caratRange: "0.50 - 3.00 ct",
    quality: "D-G Color, IF-VS1",
    image: "https://images.pexels.com/photos/2735981/pexels-photo-2735981.jpeg?auto=compress&w=500&h=500&fit=crop"
  },
  {
    id: 3,
    shape: "Emerald Cut",
    caratRange: "0.50 - 4.00 ct",
    quality: "D-F Color, IF-VVS2",
    image: "https://images.pexels.com/photos/2732096/pexels-photo-2732096.jpeg?auto=compress&w=500&h=500&fit=crop"
  },
  {
    id: 4,
    shape: "Oval",
    caratRange: "0.30 - 3.00 ct",
    quality: "D-H Color, IF-VS2",
    image: "https://images.pexels.com/photos/2849742/pexels-photo-2849742.jpeg?auto=compress&w=500&h=500&fit=crop"
  },
  {
    id: 5,
    shape: "Pear Shaped",
    caratRange: "0.50 - 3.00 ct",
    quality: "D-G Color, IF-VS1",
    image: "https://images.pexels.com/photos/6358515/pexels-photo-6358515.jpeg?auto=compress&w=500&h=500&fit=crop"
  },
  {
    id: 6,
    shape: "Cushion Cut",
    caratRange: "0.50 - 4.00 ct",
    quality: "D-F Color, IF-VVS2",
    image: "https://images.unsplash.com/photo-1770722272510-ef28c6f57541?w=500&h=500&fit=crop"
  }
];

// ---- MANUFACTURING IMAGES ----
// Replace these URLs to change the workshop/manufacturing images
export const manufacturingImages = {
  jewelry: [
    "https://images.unsplash.com/photo-1764760214881-69e2a3198c11?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1772442125267-7640b4b5f2fe?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1772442125263-c9dd28bbd938?w=800&h=600&fit=crop"
  ],
  diamond: [
    "https://images.unsplash.com/photo-1764760214881-69e2a3198c11?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3?w=800&h=600&fit=crop"
  ]
};

export const nyImage = "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?w=1200&h=800&fit=crop";
export const nySkyline = "https://images.unsplash.com/photo-1613968317130-e6565f71b9d3?w=1920&h=600&fit=crop";
